import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { alpacaService } from '../../services/AlpacaService';
import TradeCalendar from '../../components/TradeCalendar/TradeCalendar';
import type { CalendarResponse } from '../../components/TradeCalendar/calendarUtils';
import styles from './TradeCalendarPage.module.css';

type Mode = 'live' | 'paper';

function monthRange(monthOffset: number): { from: string; to: string; label: string } {
  // monthOffset: 0 = current month, -1 = last month, etc.
  const now = new Date();
  const target = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const start = new Date(target.getFullYear(), target.getMonth(), 1);
  const end = new Date(target.getFullYear(), target.getMonth() + 1, 0);
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const label = target.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  return { from: fmt(start), to: fmt(end), label };
}

const TradeCalendarPage: React.FC = () => {
  const [mode, setMode] = useState<Mode>('live');
  const [monthOffset, setMonthOffset] = useState(0);
  const [data, setData] = useState<CalendarResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const range = useMemo(() => monthRange(monthOffset), [monthOffset]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await alpacaService.getLiveTradeCalendar(range.from, range.to, mode);
      setData(resp);
    } catch (err: any) {
      setError(err?.message || 'Failed to load trade calendar');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, mode]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Summary chips computed from the response ──
  const summary = useMemo(() => {
    if (!data || data.days.length === 0) {
      return { trades: 0, totalPnl: 0, winDays: 0, lossDays: 0, bestDay: 0, worstDay: 0 };
    }
    let trades = 0;
    let totalPnl = 0;
    let winDays = 0;
    let lossDays = 0;
    let bestDay = -Infinity;
    let worstDay = Infinity;
    for (const d of data.days) {
      trades += d.tradeCount;
      totalPnl += d.netPnl;
      if (d.netPnl > 0) winDays += 1;
      else if (d.netPnl < 0) lossDays += 1;
      if (d.netPnl > bestDay) bestDay = d.netPnl;
      if (d.netPnl < worstDay) worstDay = d.netPnl;
    }
    if (bestDay === -Infinity) bestDay = 0;
    if (worstDay === Infinity) worstDay = 0;
    return { trades, totalPnl, winDays, lossDays, bestDay, worstDay };
  }, [data]);

  const pnlClass = (v: number) => (v > 0 ? styles.pnlPos : v < 0 ? styles.pnlNeg : styles.pnlFlat);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Trade Calendar</h1>
          <p className={styles.subtitle}>
            Daily realized P&amp;L from your broker fills, paired into round-trips with FIFO matching. Click a day to
            see the trades that closed.
          </p>
        </div>
        <div className={styles.controls}>
          <div className={styles.modeToggle} role="tablist" aria-label="Account mode">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'live'}
              className={`${styles.modeBtn} ${mode === 'live' ? styles.modeBtnActive : ''}`}
              onClick={() => setMode('live')}
            >
              Live
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'paper'}
              className={`${styles.modeBtn} ${mode === 'paper' ? styles.modeBtnActive : ''}`}
              onClick={() => setMode('paper')}
            >
              Paper
            </button>
          </div>
          <div className={styles.monthNav}>
            <button
              type="button"
              className={styles.monthBtn}
              onClick={() => setMonthOffset((o) => o - 1)}
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className={styles.monthLabel}>{range.label}</span>
            <button
              type="button"
              className={styles.monthBtn}
              onClick={() => setMonthOffset((o) => o + 1)}
              disabled={monthOffset >= 0}
              aria-label="Next month"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Net P&amp;L this month</div>
          <div className={`${styles.summaryValue} ${pnlClass(summary.totalPnl)}`}>
            {summary.totalPnl >= 0 ? '+' : ''}${summary.totalPnl.toFixed(2)}
          </div>
          <div className={styles.summaryHint}>{summary.trades} round-trips</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Days in profit</div>
          <div className={styles.summaryValue}>
            {summary.winDays}W / {summary.lossDays}L
          </div>
          <div className={styles.summaryHint}>
            {summary.winDays + summary.lossDays > 0
              ? `${Math.round((summary.winDays / (summary.winDays + summary.lossDays)) * 100)}% win-day rate`
              : 'No closed trades yet'}
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Best / Worst day</div>
          <div className={styles.summaryValue}>
            <span className={styles.pnlPos}>+${summary.bestDay.toFixed(2)}</span>
            {' / '}
            <span className={styles.pnlNeg}>${summary.worstDay.toFixed(2)}</span>
          </div>
          <div className={styles.summaryHint}>biggest swings this month</div>
        </div>
      </div>

      <TradeCalendar
        data={data}
        loading={loading}
        error={error}
        onRetry={() => {
          setData(null);
          setTimeout(() => load(), 0);
        }}
      />
    </div>
  );
};

export default TradeCalendarPage;
